import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Check, CheckCheck, MessageSquare, MoreVertical, RefreshCw, Search } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { customersApi } from '../../../api';
import { useUIStore } from '../../../store';

dayjs.extend(relativeTime);

// WhatsApp Light Theme Colors
const WHATSAPP_GREEN = '#25d366';
const WHATSAPP_TEAL = '#128c7e';
const WHATSAPP_DARK_TEAL = '#075e54';
const WHATSAPP_LIGHT_GREEN = '#dcf8c6';
const WHATSAPP_CHAT_BG = '#e5ddd5';
const WHATSAPP_SIDEBAR_BG = '#ffffff';
const WHATSAPP_HOVER = '#f5f5f5';
const WHATSAPP_SELECTED = '#ebebeb';
const WHATSAPP_BORDER = '#e0e0e0';
const WHATSAPP_SEARCH_BG = '#f6f6f6';

const MEDIA_TYPE_LABELS = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  document: 'Document',
};

const MEDIA_TYPE_SET = new Set(Object.keys(MEDIA_TYPE_LABELS));

const MEDIA_EXTENSIONS = {
  image: new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']),
  video: new Set(['mp4', 'webm', 'ogg', '3gp', 'mov', 'mkv']),
  audio: new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac', 'opus']),
  document: new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt']),
};

const getMessageText = (message) =>
  message?.text || message?.message_text || message?.message || message?.content || '';

const normalizeUrl = (value) => String(value || '').trim();

const isHttpUrl = (value) => /^https?:\/\//i.test(normalizeUrl(value));

const getFileExtension = (value) => {
  if (!value) return '';
  const clean = String(value).split('?')[0].split('#')[0];
  const lastDot = clean.lastIndexOf('.');
  if (lastDot === -1) return '';
  return clean.slice(lastDot + 1).toLowerCase();
};

const isLikelyMediaUrl = (value) => {
  const normalized = normalizeUrl(value);
  if (!normalized) return false;
  if (/^(https?:\/\/|data:|blob:)/i.test(normalized)) return true;
  if (normalized.startsWith('/')) return true;
  return Boolean(getFileExtension(normalized));
};

const getFileNameFromUrl = (value) => {
  if (!value) return '';
  const clean = String(value).split('?')[0].split('#')[0];
  const parts = clean.split('/');
  return parts[parts.length - 1] || '';
};

const guessCategoryFromExtension = (url) => {
  const ext = getFileExtension(url);
  if (!ext) return null;
  if (MEDIA_EXTENSIONS.image.has(ext)) return 'image';
  if (MEDIA_EXTENSIONS.video.has(ext)) return 'video';
  if (MEDIA_EXTENSIONS.audio.has(ext)) return 'audio';
  if (MEDIA_EXTENSIONS.document.has(ext)) return 'document';
  return null;
};

const inferMediaCategory = (rawType, url) => {
  const normalized = String(rawType || '').toLowerCase();
  if (MEDIA_TYPE_SET.has(normalized)) return normalized;
  if (normalized.startsWith('image/')) return 'image';
  if (normalized.startsWith('video/')) return 'video';
  if (normalized.startsWith('audio/')) return 'audio';
  if (
    normalized.includes('pdf') ||
    normalized.includes('word') ||
    normalized.includes('excel') ||
    normalized.includes('presentation') ||
    normalized.includes('text/')
  ) {
    return 'document';
  }
  return guessCategoryFromExtension(url);
};

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getMediaUrlFromObject = (media) =>
  normalizeUrl(
    media?.url ||
      media?.link ||
      media?.href ||
      media?.media_url ||
      media?.mediaUrl ||
      media?.file ||
      media?.file_url ||
      media?.fileUrl
  );

const resolveMediaDetails = (message) => {
  if (!message) return null;

  const text = getMessageText(message);
  const rawMedia =
    message.media ||
    message.media_url ||
    message.mediaUrl ||
    message.file_url ||
    message.fileUrl ||
    message.media_link ||
    message.mediaLink ||
    null;

  let mediaObj = null;
  let url = '';

  if (rawMedia && typeof rawMedia === 'object') {
    mediaObj = rawMedia;
  } else if (typeof rawMedia === 'string') {
    const trimmed = rawMedia.trim();
    if (trimmed) {
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        const parsed = safeParseJson(trimmed);
        if (parsed) {
          mediaObj = parsed;
        } else {
          url = trimmed;
        }
      } else {
        url = trimmed;
      }
    }
  }

  if (Array.isArray(mediaObj)) {
    mediaObj = mediaObj[0];
  }

  if (mediaObj) {
    url = getMediaUrlFromObject(mediaObj) || url;
  }

  if (url && !isLikelyMediaUrl(url)) {
    url = '';
  }

  const rawType =
    mediaObj?.type ||
    mediaObj?.media_type ||
    mediaObj?.mimeType ||
    mediaObj?.contentType ||
    message.media_type ||
    message.mediaType ||
    message.type ||
    '';
  const typeHint = inferMediaCategory(rawType, url);
  const textUrl = text && isHttpUrl(text) ? normalizeUrl(text) : '';
  const textCategory = textUrl ? guessCategoryFromExtension(textUrl) : null;

  const hasMediaHint = Boolean(rawMedia) || Boolean(typeHint) || Boolean(textCategory);
  if (!url && textUrl) {
    url = textUrl;
  }

  if (!url || !hasMediaHint) return null;

  const category = typeHint || textCategory || guessCategoryFromExtension(url);
  const fileName =
    mediaObj?.file_name ||
    mediaObj?.fileName ||
    mediaObj?.filename ||
    mediaObj?.name ||
    message.file_name ||
    message.fileName ||
    getFileNameFromUrl(url) ||
    'File';
  const mimeType = mediaObj?.mimeType || mediaObj?.contentType || '';

  return {
    url,
    category,
    mimeType,
    fileName,
  };
};

const getMediaLabel = (category) => MEDIA_TYPE_LABELS[category] || 'Media';

const formatChatDate = (value) => {
  if (!value) return '';
  const date = dayjs(value);
  if (!date.isValid()) return '';
  const now = dayjs();
  if (date.isSame(now, 'day')) return date.format('HH:mm');
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (date.isAfter(now.subtract(7, 'day'))) return date.format('dddd');
  return date.format('DD/MM/YYYY');
};

const getPreviewText = (message) => {
  if (!message) return '';
  const text = getMessageText(message);
  const media = resolveMediaDetails(message);
  const normalizedText = normalizeUrl(text);
  if (text && (!media || normalizedText !== normalizeUrl(media.url))) return text;
  if (media) return getMediaLabel(media.category);
  return message.type ? `[${message.type}]` : '';
};

const truncateText = (value, maxLength = 40) => {
  const text = String(value || '');
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const renderMediaPreview = (media) => {
  if (!media?.url) return null;
  if (media.category === 'image') {
    return (
      <Box
        component="a"
        href={media.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'block', textDecoration: 'none' }}
      >
        <Box
          component="img"
          src={media.url}
          alt={media.fileName || 'Image'}
          sx={{
            width: '100%',
            maxHeight: 260,
            objectFit: 'contain',
            borderRadius: 1,
            bgcolor: '#f5f5f5',
          }}
        />
      </Box>
    );
  }
  if (media.category === 'video') {
    return (
      <Box component="video" controls sx={{ width: '100%', maxHeight: 260, borderRadius: 1 }}>
        <source src={media.url} type={media.mimeType || 'video/mp4'} />
        Your browser does not support the video tag.
      </Box>
    );
  }
  if (media.category === 'audio') {
    return (
      <Box component="audio" controls sx={{ width: '100%' }}>
        <source src={media.url} type={media.mimeType || 'audio/mpeg'} />
        Your browser does not support the audio element.
      </Box>
    );
  }
  if (media.category === 'document') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          p: 1,
          borderRadius: 1,
          bgcolor: 'rgba(0,0,0,0.05)',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {truncateText(media.fileName || 'Document', 42)}
        </Typography>
        <Box
          component="a"
          href={media.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', fontSize: '0.8rem', textDecoration: 'none' }}
        >
          Open document
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="a"
      href={media.url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: 'primary.main', fontSize: '0.85rem', textDecoration: 'none' }}
    >
      View attachment
    </Box>
  );
};

const InboxManagementPage = () => {
  const theme = useTheme();
  const { showError } = useUIStore();
  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    setListLoading(true);
    try {
      const response = await customersApi.getAllConversations();
      const payload = response?.data?.data ?? response?.data ?? [];
      const list = Array.isArray(payload) ? payload : [];
      setConversations(list);
      if (!selectedCustomerId) {
        const firstWithId = list.find((entry) => entry?.customer?.id);
        if (firstWithId?.customer?.id) {
          setSelectedCustomerId(firstWithId.customer.id);
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      showError('Failed to load conversations');
    } finally {
      setListLoading(false);
    }
  };

  const loadConversationDetail = async (customerId) => {
    if (!customerId) return;
    setDetailLoading(true);
    try {
      const response = await customersApi.getConversation(customerId);
      const payload = response?.data?.data ?? response?.data ?? {};
      setSelectedConversation(payload || null);
      setMessages(Array.isArray(payload?.messages) ? payload.messages : []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      showError('Failed to load conversation');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      loadConversationDetail(selectedCustomerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);

  const filteredConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((item) => {
      const name = String(item?.customer?.name || '').toLowerCase();
      const number = String(item?.customer?.number || '').toLowerCase();
      return name.includes(query) || number.includes(query);
    });
  }, [conversations, search]);

  const selectedCustomer = selectedConversation?.conversation?.customer;

  const headerGradient = 'linear-gradient(110deg, #25d366, #1ebe5a)';
  const headerText = '#ffffff';

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', bgcolor: '#f0f2f5', borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      {/* Left Sidebar - Chat List */}
      <Box
        sx={{
          width: 400,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: WHATSAPP_SIDEBAR_BG,
          borderRight: `1px solid ${WHATSAPP_BORDER}`,
        }}
      >
        {/* Sidebar Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: headerGradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 60,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: headerText,
                color: '#1ebe5a',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              <MessageSquare size={20} />
            </Avatar>
            <Typography sx={{ color: headerText, fontWeight: 600, fontSize: '1.1rem' }}>
              Inbox
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" sx={{ color: headerText }} onClick={loadConversations}>
              <RefreshCw size={20} />
            </IconButton>
            <IconButton size="small" sx={{ color: headerText }}>
              <MoreVertical size={20} />
            </IconButton>
          </Stack>
        </Box>

        {/* Search Box */}
        <Box sx={{ px: 1.5, py: 1, bgcolor: '#f6f6f6', borderBottom: `1px solid ${WHATSAPP_BORDER}` }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search or start new chat"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#54656f" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: 2,
                '& fieldset': { border: 'none' },
                '& input': { color: '#111b21', py: 1, '&::placeholder': { color: '#667781', opacity: 1 } },
              },
            }}
          />
        </Box>

        {/* Chat List */}
        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#fff' }}>
          {listLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress size={28} sx={{ color: WHATSAPP_TEAL }} />
            </Stack>
          ) : filteredConversations.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <MessageSquare size={48} color="#667781" />
              <Typography variant="body2" sx={{ color: '#667781', mt: 2 }}>
                No conversations found
              </Typography>
            </Stack>
          ) : (
            <List disablePadding>
              {filteredConversations.map((item) => {
                const customer = item.customer || {};
                const isActive = customer.id === selectedCustomerId;
                const lastMessage = item.lastMessage || null;
                return (
                  <ListItemButton
                    key={`${customer.id || 'unknown'}-${customer.number || ''}`}
                    selected={isActive}
                    onClick={() => customer.id && setSelectedCustomerId(customer.id)}
                    disabled={!customer.id}
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: isActive ? WHATSAPP_SELECTED : 'transparent',
                      borderBottom: `1px solid ${WHATSAPP_BORDER}`,
                      '&:hover': { bgcolor: WHATSAPP_HOVER },
                      '&.Mui-selected': { bgcolor: WHATSAPP_SELECTED },
                      '&.Mui-selected:hover': { bgcolor: WHATSAPP_SELECTED },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: '#dfe5e7',
                        color: '#fff',
                        fontSize: '1.25rem',
                        fontWeight: 500,
                      }}
                    >
                      {String(customer.name || customer.number || 'C').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          sx={{
                            color: '#111b21',
                            fontWeight: 500,
                            fontSize: '1rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {customer.name || customer.number || 'Unknown'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#667781', fontSize: '0.75rem', ml: 1, flexShrink: 0 }}
                        >
                          {formatChatDate(item.lastActivity)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" sx={{ mt: 0.25 }}>
                        <CheckCheck size={16} color="#53bdeb" style={{ marginRight: 4, flexShrink: 0 }} />
                        <Typography
                          sx={{
                            color: '#667781',
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {truncateText(getPreviewText(lastMessage) || 'No messages yet')}
                        </Typography>
                      </Stack>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Box>

      {/* Right Side - Chat Window */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: WHATSAPP_CHAT_BG }}>
        {!selectedCustomerId ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f0f2f5',
              borderBottom: `6px solid ${WHATSAPP_TEAL}`,
            }}
          >
            <Box
              sx={{
                width: 300,
                height: 300,
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MessageSquare size={150} color="#25d366" strokeWidth={1} />
            </Box>
            <Typography variant="h5" sx={{ color: '#41525d', fontWeight: 300, mb: 1 }}>
              Inbox Management
            </Typography>
            <Typography sx={{ color: '#667781', textAlign: 'center', maxWidth: 450, lineHeight: 1.6 }}>
              View and manage all your customer conversations.
              <br />
              Select a chat from the left to view messages.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Chat Header */}
            <Box
              sx={{
                px: 2,
                py: 1,
                background: headerGradient,
                display: 'flex',
                alignItems: 'center',
                minHeight: 60,
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: '#dfe5e7',
                  color: '#fff',
                  fontSize: '1rem',
                }}
              >
                {String(selectedCustomer?.name || selectedCustomer?.number || 'C').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ ml: 2, flex: 1 }}>
                <Typography sx={{ color: headerText, fontWeight: 500, fontSize: '1rem' }}>
                  {selectedCustomer?.name || 'Customer'}
                </Typography>
                <Typography variant="caption" sx={{ color: headerText, opacity: 0.8 }}>
                  {selectedCustomer?.number || 'click here for contact info'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton size="small" sx={{ color: headerText }} onClick={() => loadConversationDetail(selectedCustomerId)}>
                  <RefreshCw size={20} />
                </IconButton>
                <IconButton size="small" sx={{ color: headerText }}>
                  <MoreVertical size={20} />
                </IconButton>
              </Stack>
            </Box>

            {/* Messages Area */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: { xs: 2, md: 8 },
                py: 2,
                bgcolor: WHATSAPP_CHAT_BG,
                backgroundImage: `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFQAAABUCAMAAADWnVnrAAAA21BMVEUAAAD////t9PHy9vT19/bx9fP3+fj6+/v5+vr09vXw9PLu8/H7/Pz8/f3z9fTv9PL+/v79/f3r8e/o7uzz+Pbp7+3n7evl6+nm7Ori6efg5+Xd5OLa4t/X4N3U3drR29jO2NXL1tPI1NDF0s7Cz8u/zcm8y8a5yMO2xsC0xL6xwbuvv7isvLWpu7OnubGltrCjtK6hsa2erb6erfGerbWbrqqYqqaVp6KSo56Pn5qMnJeLm5WHl5KElI+BkYt+joZ7i4R4iH91hXpygnZvfnJsenBrZGhjXl1ZVVP4F99uAAAANXRSTlMACQkJCQkJGBgYGBgYJycnJyc2NjY2RkZGRlVVVVVkZGRkZHNzc3OCgoKCkpKSkqGhoaGwj8FfngAABJxJREFUeF6NlIdygzAQRYMxGBuMe++999577/n/L0oAy9L5ksxk8kYI7uru3REOFfyRn2k0IVxPy9O/wkefxqH8i9X6qJBuAi8f9dcFnukPMhAhK+AnJIHxSsRHK+F4FMaWyLfMDnTkCYgdJIgJhYJEniKcbcAgkKjRCNYVOhIpyUhJHpd4WEBknqjMgI9FAsdqFH+QThBM0ChGH+EDMkIpWhf+AOSEPFAHh6T4AAOEIk1H7GFIJiLYwCuIx2kUkYVfwqnCOaGP0WK0S3BMIDcb/4pqbHzGJGGgkOgAM4UScxNEpMTVvxDg/C9ZtLfHIJmoLGRSWDJdcRwrYcNR5O9O2VQ6Ik5ShMBvTdG0I9aOGQ0tUjyqhHlqJElQ0jqQS1HJXMmNcAjJ5JBPVHaFQkqitINIdqmFQlKkJItU9smMGUmJBGRSTiYkaRKkyAqFdGYUPyLt8EwKU0IheeyQLFJIEoRCirKQqD6TRmSS/xSbquxIEHlKEoSAipKEYgiZkCgJM0pJksQJifJIkgSJNASJREoMEwQhJAodkIggEQQhFBIkhQqJkkIhJEKiJKEIQigkSgoJIhRSSCKFChKJCgVCIYUKiQoSiQoFQiGFComSgsgnlA9//0TGp/KDvDfj2sYYH3jO77iWT5A3AncEyJmQCJ3/OgfI2SZ+7wLMkOMpAq5qlCgOQAIhgRI8ESYACRBQH/gqBOaJkiB0YYGcYwGHygVCHyU4F5jgfKSCcw5QYMJJ5wvnE6HgCucL5xyBc1LBkwZwnvAtPBV87RvI+YLQAJ4IPgdPFXAe+M4HZwzgvAecD0BB8MyHe+F5sH3v2h7bAPY9a3vgnb7s4QcO6PJEPqAq6TzwZAFPA+8JnHNAn68NnAu+NnDOAe/xYI9HAc6FeXbvXe8BPuYDPPa+A/y4x/t4HhiYZwfmOb6HlwBk78FzzY9xYN4FzI9xYN6F17q+B955L/u+d33gTQCe1/Y9vOy91wb27oPv+T4+cNkDzwae2/c937m+1/c9H+/2PV7u+n5AljwftCXPB2rJC0Ba8oIAacmTCYK2pH8IhBYkIBCqD0lBAnJNkEBqQSCQ0oIgkNKCIHRuQRC6kNSCQOhCUgtCRnMSCM1JIKA1CQTWJCC0JhCa0YJAaEYLgkBrYhBaE4PWBCG0phsJnckNQmtyg9Ca3EhoTgikJncldCa3yM3EIPJfZjcTg9i65SYyG4TW5AZCC5KCcbIELQgtSDomybqx3EhsQb3hCQO5kbmR0JxcIDcyNxJakBOEnp/Ym4hBaE4ugJacSGhOLrAmjhCak3OsSSPE1uQcaxIIzck51sQhNif/sj0KkJvTFfZHAXJzuuL+KEhXxCL2h2G6IhaxPwzTFbGI/WGYrpD+cH+Upquk39k/TpeWwLD7vzb3uHD4cH/U5t4uyv17Q79H+Yd5n+C3J/h9vu7T+ry9h/iw5D+z3/nz/J5/2Bv65/lz/u1/aH+ev8h/Xb/oP9b/4H/v+tf1P/q/+L/5j/F/4v/2d/G/6z/W/+7/QP9H+r/8H/j/1h/S/+0dIh0imSP8AxKJZJH0TL8AAAAASUVORK5CYII=")`,
                backgroundRepeat: 'repeat',
              }}
            >
              {detailLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} sx={{ color: WHATSAPP_TEAL }} />
                </Stack>
              ) : messages.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: '#fff8e1',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      color: '#856404',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    No messages in this conversation yet
                  </Paper>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  {messages.map((message, index) => {
                    const isIncoming = String(message.direction || '').toUpperCase() === 'IN';
                    const showDate = index === 0 || !dayjs(message.createdAt).isSame(dayjs(messages[index - 1]?.createdAt), 'day');
                    const media = resolveMediaDetails(message);
                    const messageText = getMessageText(message);
                    const showText = messageText && (!media || normalizeUrl(messageText) !== normalizeUrl(media.url));
                    const mediaFallback =
                      !media && message.media
                        ? (typeof message.media === 'string'
                          ? message.media
                          : message.media?.name || message.media?.filename || 'Media attachment')
                        : '';

                    return (
                      <React.Fragment key={message.id || `${message.createdAt}-${index}`}>
                        {showDate && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Paper
                              elevation={0}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.95)',
                                borderRadius: 2,
                                px: 2,
                                py: 0.5,
                                boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#54656f', fontSize: '0.75rem' }}>
                                {dayjs(message.createdAt).format('MMMM D, YYYY')}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: isIncoming ? 'flex-start' : 'flex-end',
                            mb: 0.25,
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              maxWidth: '65%',
                              minWidth: 80,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: isIncoming ? '#fff' : WHATSAPP_LIGHT_GREEN,
                              color: '#111b21',
                              position: 'relative',
                              borderTopLeftRadius: isIncoming ? 0 : 8,
                              borderTopRightRadius: isIncoming ? 8 : 0,
                              boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                              '&::before': isIncoming ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: -8,
                                width: 8,
                                height: 13,
                                bgcolor: '#fff',
                                clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
                              } : {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: -8,
                                width: 8,
                                height: 13,
                                bgcolor: WHATSAPP_LIGHT_GREEN,
                                clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                              },
                            }}
                          >
                            {media && (
                              <Box sx={{ mb: showText ? 0.5 : 0 }}>
                                {renderMediaPreview(media)}
                              </Box>
                            )}
                            {showText && (
                              <Typography
                                sx={{
                                  fontSize: '0.9rem',
                                  lineHeight: 1.4,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  color: '#111b21',
                                }}
                              >
                                {messageText}
                              </Typography>
                            )}
                            {mediaFallback && (
                              <Typography variant="caption" sx={{ color: '#667781', display: 'block' }}>
                                Media: {truncateText(mediaFallback, 60)}
                              </Typography>
                            )}
                            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#667781',
                                  fontSize: '0.65rem',
                                }}
                              >
                                {message.createdAt ? dayjs(message.createdAt).format('HH:mm') : ''}
                              </Typography>
                              {!isIncoming && (
                                <CheckCheck size={16} color="#53bdeb" />
                              )}
                            </Stack>
                          </Paper>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Stack>
              )}
            </Box>

            {/* Input Area - Read Only Notice */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: '#f0f2f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderTop: `1px solid ${WHATSAPP_BORDER}`,
              }}
            >
              <Typography sx={{ color: '#667781', fontSize: '0.875rem' }}>
                This is a read-only inbox view. Use Live Chat to send messages.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default InboxManagementPage;
